import { loginRequest, getProfile } from "../services/authService";
import { setMultimonedaFlag } from "../../../shared/hooks/useMultimoneda";
import { setTenantTipo } from "../../../shared/hooks/useTenantTipo";
import apiFetch from "../../../shared/services/apiClient";

export function useAuth() {
  async function login(username, password) {
    const data = await loginRequest(username, password);

    // Wipe every key from any previous session before writing new user's data.
    // This prevents role/condominio data from a previous user leaking into the
    // new session when two different accounts are used in the same browser.
    localStorage.clear();

    localStorage.setItem("access_token", data.access);
    localStorage.setItem("refresh_token", data.refresh);
    localStorage.setItem("login_at", String(Date.now()));

    const perfil = await getProfile();
    const uc  = perfil?.usuario_condominio;
    const rol = uc?.rol || "USUARIO";

    localStorage.setItem("is_superuser", perfil?.is_superuser ? "true" : "false");
    localStorage.setItem("rol", rol);

    if (rol === "CONTADOR") {
      localStorage.setItem("theme", "dark");
      const condominios = uc?.condominios_contador || [];
      localStorage.setItem("condominios_contador", JSON.stringify(condominios));
      // Restore last selected condominio or default to first
      const savedId  = localStorage.getItem("active_condominio_id");
      const validIds = condominios.map(c => String(c.id));
      let activeId   = savedId && validIds.includes(savedId) ? savedId : String(condominios[0]?.id ?? "");
      if (!savedId || !validIds.includes(savedId)) {
        if (condominios.length > 0) {
          localStorage.setItem("active_condominio_id", activeId);
        }
      }
      // Sync multimoneda del condominio activo
      const activeCondo = condominios.find(c => String(c.id) === String(activeId));
      setMultimonedaFlag(!!activeCondo?.multimoneda_activa);
      setTenantTipo(activeCondo?.tipo || "condominio");
    } else if (rol === "RESIDENTE") {
      localStorage.setItem("theme", "light");
      if (uc?.filial_id) localStorage.setItem("filial_id", String(uc.filial_id));
      if (uc?.filial_nombre) localStorage.setItem("filial_nombre", uc.filial_nombre);
      if (uc?.condominio_nombre) localStorage.setItem("condominio_nombre", uc.condominio_nombre);
      if (perfil?.first_name) localStorage.setItem("first_name", perfil.first_name);
      if (perfil?.last_name) localStorage.setItem("last_name", perfil.last_name);
    } else {
      localStorage.setItem("theme", "dark");
      const condominioId = uc?.condominio?.id;
      if (condominioId) {
        localStorage.setItem("condominioId", String(condominioId));
        setMultimonedaFlag(!!(uc?.condominio?.multimoneda_activa));
        setTenantTipo(uc?.condominio?.tipo || "condominio");
      } else if (perfil?.is_superuser) {
        // Superadmin sin condominio propio: cargar todos los condominios para el
        // selector global, de modo que pueda navegar y probar el sistema real.
        try {
          const res    = await apiFetch("/api/core/admin/condominios/");
          const data   = res.ok ? await res.json() : [];
          const list   = Array.isArray(data) ? data : [];
          localStorage.setItem("condominios_admin", JSON.stringify(list));
          const validIds = list.map(c => String(c.id));
          const savedId  = localStorage.getItem("active_condominio_id");
          const activeId = savedId && validIds.includes(savedId)
            ? savedId
            : String(list[0]?.id ?? "");
          if (activeId) localStorage.setItem("active_condominio_id", activeId);
          const active = list.find(c => String(c.id) === String(activeId));
          setMultimonedaFlag(!!active?.multimoneda_activa);
          setTenantTipo(active?.tipo || "condominio");
        } catch { /* el panel admin sigue funcionando sin selector */ }
      } else {
        setMultimonedaFlag(!!(uc?.condominio?.multimoneda_activa));
        setTenantTipo(uc?.condominio?.tipo || "condominio");
      }
    }

    return data;
  }

  function logout() {
    localStorage.clear();
    // replace() avoids adding /login to the browser history stack so the user
    // can't navigate "back" into a protected page after logging out.
    // The timestamp query param forces the browser to discard any cached copy
    // of the page, ensuring a full asset reload.
    window.location.replace("/login?_=" + Date.now());
  }

  function isAuthenticated() {
    const token = localStorage.getItem("access_token");
    const refresh = localStorage.getItem("refresh_token");
    return Boolean(token && refresh);
  }

  return { login, logout, isAuthenticated };
}
